import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, Edit, Trash2, X } from 'lucide-react';
import { QuickCreateVendor, QuickCreateProduct } from '../../components/QuickCreateModals';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [indents, setIndents] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusForm, setStatusForm] = useState({ status: '', notes: '' });
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [currencyRate, setCurrencyRate] = useState(1);
  const [formData, setFormData] = useState({
    vendor_id: '',
    indent_id: '',
    delivery_date: '',
    payment_terms: 'Net 30',
    currency: 'UGX',
    notes: '',
    items: [{ sku: '', name: '', quantity: 0, unit: '', unit_price: 0, vat_rate: 0 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, vendorsRes, indentsRes, inventoryRes, curRes] = await Promise.all([
        api.get('/purchase/orders'),
        api.get('/purchase/vendors'),
        api.get('/purchase/indents?status=approved'),
        api.get('/sales/inventory'),
        api.get('/config/currency').catch(() => ({ data: { usd_to_ugx_rate: 1 } }))
      ]);
      setOrders(ordersRes.data);
      setVendors(vendorsRes.data);
      setIndents(indentsRes.data);
      setInventory(inventoryRes.data);
      setCurrencyRate(curRes.data?.usd_to_ugx_rate || 1);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateItemTotals = (item) => {
    const taxable = item.quantity * item.unit_price;
    const vatAmount = (taxable * (item.vat_rate || 0)) / 100;
    const total = taxable + vatAmount;
    return { ...item, taxable_amount: taxable, vat_amount: vatAmount, total_amount: total, received_quantity: 0 };
  };

  const changeCurrency = (newCurrency) => {
    const rate = currencyRate || 1;
    let items = formData.items;
    if (newCurrency !== formData.currency && rate && rate !== 1) {
      const factor = newCurrency === 'USD' ? (1 / rate) : rate;
      items = formData.items.map(it => ({ ...it, unit_price: Math.round((it.unit_price || 0) * factor * 100) / 100 }));
    }
    setFormData({ ...formData, currency: newCurrency, items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const itemsWithTotals = formData.items.map(calculateItemTotals);
      const exchange_rate = formData.currency === 'USD' ? (currencyRate || 1) : 1;
      const res = await api.post('/purchase/orders', { ...formData, items: itemsWithTotals, exchange_rate });
      // Auto-submit for workflow approval
      try {
        await api.post('/workflow/approvals/request', {
          document_type: 'purchase_order', document_id: res.data.id,
          document_number: res.data.po_number, module: 'purchase', max_level: 3
        });
      } catch (wfErr) { /* Workflow optional - don't block PO creation */ }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const openStatusModal = (order) => {
    setEditingOrder(order);
    setStatusForm({ status: order.status, notes: order.notes || '' });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/purchase/orders/${editingOrder.id}`, statusForm);
      setShowStatusModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update PO');
    }
  };

  const deleteOrder = async (id, poNumber) => {
    if (!window.confirm(`Are you sure you want to delete PO "${poNumber}"?`)) return;
    try {
      await api.delete(`/purchase/orders/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete PO');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { sku: '', name: '', quantity: 0, unit: '', unit_price: 0, vat_rate: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'sku' && value) {
      const invItem = inventory.find(i => i.sku === value);
      if (invItem) {
        newItems[index].name = invItem.name;
        newItems[index].unit = invItem.unit;
        newItems[index].unit_price = invItem.unit_price || 0;
        newItems[index].vat_rate = invItem.vat_rate || 0;
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  const getStatusBadge = (status) => {
    const badges = { pending: 'badge-warning', approved: 'badge-success', partial: 'badge-info', received: 'badge-success', cancelled: 'badge-danger' };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.vendor_gstin && order.vendor_gstin.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div data-testid="purchase-orders-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Purchase Orders</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/purchase-orders" filename="purchase_orders_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/purchase-orders" filename="purchase_orders_export.pdf" />
          <button onClick={() => setShowModal(true)} className="btn btn-primary" data-testid="create-po-btn">
            <Plus size={16} />
            Create PO
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search by PO #, vendor, or VAT No..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '40px' }}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="partial">Partial</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading purchase orders...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Vendor</th>
                  <th>Items</th>
                  <th className="text-right">Total Amount</th>
                  <th>Delivery Date</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} data-testid={`po-row-${order.po_number}`}>
                    <td className="font-mono">{order.po_number}</td>
                    <td>{order.vendor_name}</td>
                    <td>{order.items?.length || 0} items</td>
                    <td className="text-right tabular-nums">{order.currency || 'UGX'} {(order.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="text-sm">{new Date(order.delivery_date).toLocaleDateString()}</td>
                    <td>
                      <span className={getStatusBadge(order.status)} data-testid={`status-${order.po_number}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openStatusModal(order)} className="btn btn-ghost btn-sm" title="Edit Status" data-testid={`edit-btn-${order.po_number}`}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => deleteOrder(order.id, order.po_number)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${order.po_number}`}>
                          <Trash2 size={14} color="#DC2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && editingOrder && (
        <div className="modal-overlay" data-testid="status-modal">
          <div className="modal-content" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Update PO {editingOrder.po_number}</h2>
            </div>
            <form onSubmit={handleStatusUpdate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} data-testid="status-select">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="partial">Partial</option>
                    <option value="received">Received</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={statusForm.notes} onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })} data-testid="status-notes-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowStatusModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="update-status-btn">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create PO Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="po-modal">
          <div className="modal-content" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create Purchase Order</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Currency</span>
                <select className="form-select" style={{ width: 'auto', fontSize: '13px', padding: '4px 10px' }} value={formData.currency} onChange={(e) => changeCurrency(e.target.value)} data-testid="currency-select">
                  <option value="UGX">UGX (base)</option>
                  <option value="USD">USD (1 USD = {currencyRate} UGX)</option>
                </select>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">
                      Vendor *
                      <button type="button" onClick={() => setShowVendorModal(true)} className="btn btn-ghost btn-sm" style={{ padding: '0 4px', marginLeft: '4px' }} data-testid="quick-vendor-btn"><Plus size={12} /></button>
                    </label>
                    <select className="form-select" value={formData.vendor_id} onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })} required data-testid="vendor-select">
                      <option value="">Select vendor...</option>
                      {vendors.map((vendor) => (<option key={vendor.id} value={vendor.id}>{vendor.name}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Link to Indent (Optional)</label>
                    <select className="form-select" value={formData.indent_id} onChange={(e) => setFormData({ ...formData, indent_id: e.target.value })} data-testid="indent-select">
                      <option value="">None</option>
                      {indents.map((indent) => (<option key={indent.id} value={indent.id}>{indent.indent_number} - {indent.department}</option>))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Delivery Date *</label>
                    <input type="date" className="form-input" value={formData.delivery_date} onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} required data-testid="delivery-date-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Terms *</label>
                    <select className="form-select" value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} data-testid="payment-terms-select">
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <div className="flex items-center justify-between" style={{ marginBottom: '4px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Items</label>
                    <button type="button" onClick={() => setShowProductModal(true)} className="btn btn-ghost btn-sm" data-testid="quick-product-btn"><Plus size={12} /> New Product</button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px' }}>SKU</th>
                          <th style={{ padding: '4px' }}>Name</th>
                          <th style={{ padding: '4px' }}>Qty</th>
                          <th style={{ padding: '4px' }}>Unit</th>
                          <th style={{ padding: '4px' }}>Price</th>
                          <th style={{ padding: '4px' }}>VAT%</th>
                          <th style={{ padding: '4px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index}>
                            <td style={{ padding: '4px' }}>
                              <select className="form-select" value={item.sku} onChange={(e) => updateItem(index, 'sku', e.target.value)} required style={{ minWidth: '100px', fontSize: '12px', padding: '4px' }}>
                                <option value="">Select...</option>
                                {inventory.map((inv) => (<option key={inv.sku} value={inv.sku}>{inv.sku}</option>))}
                              </select>
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="text" className="form-input" value={item.name} onChange={(e) => updateItem(index, 'name', e.target.value)} required style={{ minWidth: '120px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)} required style={{ width: '60px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="text" className="form-input" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)} required style={{ width: '50px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)} required style={{ width: '80px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="number" className="form-input" value={item.vat_rate} onChange={(e) => updateItem(index, 'vat_rate', parseFloat(e.target.value) || 0)} style={{ width: '50px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              {formData.items.length > 1 && (
                                <button type="button" onClick={() => removeItem(index)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} data-testid="add-item-btn">+ Add Item</button>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary" data-testid="cancel-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-po-btn">Create PO</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVendorModal && <QuickCreateVendor onCreated={(v) => { setVendors([...vendors, v]); setFormData({...formData, vendor_id: v.id}); }} onClose={() => setShowVendorModal(false)} />}
      {showProductModal && <QuickCreateProduct onCreated={(p) => { setInventory([...inventory, p]); }} onClose={() => setShowProductModal(false)} />}
    </div>
  );
};

export default PurchaseOrders;
